/**
 * Shared TypeScript types for session-related functionality
 * Used by both dictation.tsx and conversation.tsx pages
 */

export interface Patient {
  id: string; // SSN/national_id - used for display and registry lookup
  dbId?: number; // Database ID from transcriber_db - used for API calls
  name: string;
  ssn: string;
  dob: string;
  address?: string;
  postalCode?: number;
  registryName?: string;
  consentStatus?: 'active' | 'rejected' | 'not_viewed' | 'error' | 'unavailable';
  fhirPatientId?: string;
}

export type SessionMode = 'dictation' | 'conversation';

export interface RecordingState {
  isRecording: boolean;
  isInitializing: boolean;
  elapsedTime: number;
  isFinalizingTranscription: boolean;
  isSmartReviewing: boolean;
}

export interface SessionLoadState {
  isLoadingSession: boolean;
  isResumedSession: boolean;
}

export interface SummaryState {
  summary: string;
  editedSummary: string | null;
  isSavingSummary: boolean;
  isGeneratingSummary: boolean;
}

export interface TemplateState {
  selectedTemplateId: number | null;
  availableTemplates: DictationTemplate[];
}

export interface DictationTemplate {
  id: number;
  name: string;
  description: string | null;
  template_content: string;
}

export interface Language {
  code: string;
  name: string;
}

// Default language map for Icelandic names
export const LANGUAGE_NAME_MAP: Record<string, string> = {
  is: 'Íslenska',
  en: 'Enska',
  no: 'Norska',
  pl: 'Pólska',
};

// Default languages when API is unavailable
export const DEFAULT_LANGUAGES: Language[] = [
  { code: 'is', name: 'Íslenska' },
  { code: 'en', name: 'English' },
];

// Types for loading session data
export interface TranscriptionSegment {
  id?: number;
  segment_guid?: string;
  text?: string; // Optional - may be empty/missing from API
  start_time?: number;
  end_time?: number;
  speaker?: string; // Legacy field, prefer speaker_label
  speaker_label?: string; // Preferred field for diarization (doctor/patient)
  role?: string; // Legacy field, prefer speaker_label
}

// Attribution data for linking summary sentences to transcript/audio
export interface SummaryAttribution {
  [key: string]: unknown; // Flexible structure for attribution data
}

export interface LiveSessionData {
  segments?: TranscriptionSegment[];
  audio?: {
    audio_url?: string;
    duration?: number;
    patient_id?: number | null; // The session's own patient (transcriber_db id)
    user_transcript?: string;
    user_summary?: string;
    model_summary?: string;
    selected_template_id?: number;
  };
  user_summary?: {
    summary_text?: string;
    selected_template_id?: number;
    summary_attributed?: SummaryAttribution; // Attribution data for hover-to-highlight
    generation_id?: string | null; // Generation this saved summary was paired with; null if unpaired
  };
  model_summary?: {
    summary_text?: string;
    selected_template_id?: number;
    summary_attributed?: SummaryAttribution; // Attribution data for hover-to-highlight
    generation_id?: string | null; // Echoed on save so edits pair with this generation
  };
}
